import React, { useEffect, useState } from "react";
import { DeleteOutlined, EditOutlined, EyeOutlined } from "@ant-design/icons";
import { Space } from "antd";
import DataTable from "../../components/Comman/CustomTable/CommonTable";
import Button from "../../components/Button/Button";
import SideDrawer from "../../components/Comman/Drawer/SideDrawer";
import { dataElementList, deleteDataElement } from "../../actions/admin";
import { useDispatch, useSelector } from "react-redux";
import ConfirmDeleteModal from "../../components/Comman/ConfirmDeleteModal/ConfirmDeleteModal";
import { useNavigate } from "react-router-dom";
import { capitalizeFirstLetter, formatToCST } from "../../utils/comman";

const DataElements = () => {
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const user = useSelector((state: any) => state?.Auth?.user);
  const roleId = user?.user?.roleId;
  const [loading, setLoading] = useState<boolean>(true);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [selectedElementId, setSelectedUserId] = useState<number | null>(null);
  const [selectedOrganization, setSelectedOrganization] = useState<any>(null);
  const [sortConfig, setSortConfig] = useState<{
    field: string | null;
    order: "asc" | "desc" | null;
  }>({
    field: null,
    order: null,
  });
  const [deleteModelVisible, setDeleteModelVisible] = useState<boolean>(false);
  const handleRefresh = useSelector(
    (state: any) => state?.General?.updateListing
  );
  const [data, setData] = useState<any[]>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const fetchDataElementList = (params = {}) => {
    setLoading(true);
    const payload = {
      params: {
        ...params,
      },
      roleId,
    };
    dispatch(
      dataElementList(payload, (resp: any) => {
        setLoading(false);
        if (resp?.status === 200) {
          setData(resp?.data || []);
          setPagination({
            ...pagination,
            total: resp.pagination?.totalItems,
            current: resp.pagination?.currentPage,
            pageSize: resp.pagination?.pageSize,
          });
        }
      })
    );
  };

  useEffect(() => {
    fetchDataElementList({
      page: pagination.current || 1,
      limit: pagination.pageSize || 10,
    });
  }, [handleRefresh]);

  const handleIsModelOpen: any = () => {
    setIsModalOpen(!isModalOpen);
    setSelectedOrganization({});
  };

  const handleTableChange = (pagination: any, filters: any, sorter: any) => {
    let newSortConfig = { ...sortConfig };
    if (sorter && Object.keys(sorter).length > 0) {
      newSortConfig = {
        field: sorter.field,
        order: sorter.order === "ascend" ? "asc" : "desc",
      };
    }
    setSortConfig(newSortConfig);
    fetchDataElementList({
      page: pagination.current || 1,
      limit: pagination.pageSize || 10,
      ...(newSortConfig.field && newSortConfig.order
        ? {
            sort: newSortConfig.field,
            order: newSortConfig.order,
          }
        : {}),
    });
  };

  const handleDeleteModel = (id?: number) => {
    if (id) setSelectedUserId(id); // Store the selected user ID
    setDeleteModelVisible(!deleteModelVisible);
  };

  const handleUserDeletion = () => {
    setLoading(true); // Show loading indicator before making the API call
    dispatch(
      deleteDataElement(selectedElementId, (resp: any) => {
        setLoading(false); // Hide loading indicator once the API call is completed
        if (resp?.status === 200) {
          // User deleted successfully, handle UI update if needed
          handleDeleteModel();
        } else {
          handleDeleteModel();
        }
      })
    );
  };

  const handleEditClick = (record: any) => {
    handleIsModelOpen();
    // setDisableFields(true)
    setSelectedOrganization(record); // Store the selected organization's data
  };

  const columns = [
    {
      title: "DE",
      dataIndex: "DataElements_Name",
      key: "DataElements_Name",
      render: (data) => data || "-",
      sorter: true,
    },
    {
      title: "Elements Description",
      dataIndex: "DataElements_Description",
      key: "DataElements_Description",
      render: (data) => data || "-",
      sorter: true,
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "categoryId",
      render: (data) => data?.name || "-",
      sorter: true,
    },
    {
      title: "Is Budgeted",
      dataIndex: "DataElements_IsBudgeted",
      key: "DataElements_IsBudgeted",
      render: (value: boolean) => (value ? "Yes" : "No"),
    },
    {
      title: "Data Type",
      dataIndex: "DataElements_DataType",
      key: "DataElements_DataType",
      render: (text: string) => capitalizeFirstLetter(text) || "-",
    },
    {
      title: "Interval",
      dataIndex: "interval",
      key: "DataElements_IntervalValue",
      render: (value) => value?.Interval_Name || "-",
    },
    {
      title: "Last Updated",
      dataIndex: "updatedAt",
      key: "updatedAt",
      render: (value) => formatToCST(value) || "-",
    },
    {
      title: "Last Updated By",
      dataIndex: "DataElements_LastUpdatedBy",
      key: "DataElements_LastUpdatedBy",
      render: (value) => value || "-",
    },
    ...(roleId === 1
      ? [
          {
            title: "Action",
            key: "action",
            render: (record: any) => (
              <Space size="middle">
                <span
                  style={{
                    color: "#3DA57F",
                    fontWeight: "bold",
                    fontSize: "16px",
                  }}
                >
                  <EditOutlined onClick={() => handleEditClick(record)} />
                </span>
                <span
                  style={{
                    color: "#3DA57F",
                    fontWeight: "bold",
                    fontSize: "16px",
                  }}
                >
                  <EyeOutlined
                    onClick={() =>
                      navigate(`/admin/view/data-element/${record?.id}`)
                    }
                  />
                </span>
                {/* Uncomment if delete functionality is required */}
                <span
                  style={{
                    color: "#3DA57F",
                    fontWeight: "bold",
                    fontSize: "16px",
                  }}
                >
                  <DeleteOutlined
                    onClick={() => handleDeleteModel(record?.id)}
                  />
                </span>
              </Space>
            ),
          },
        ]
      : []), // If roleId is not 1, return an empty array (no action column)
  ];

  return (
    <div>
      {/* <Header /> */}

      <div className="flex justify-between">
        <h1 className="text-3xl font-bold text-left mb-6 text-white">
          Data Elements
        </h1>
        {roleId === 1 && (
          <div>
            <Button
              buttonText="Create Elements"
              padding="p-[12px] 2xl:p-[15px]"
              text="text-[12px]"
              handleIsModelOpen={handleIsModelOpen}
              type="button"
            />
          </div>
        )}
      </div>

      <div className="table-col">
        <DataTable
          columns={columns}
          data={data}
          loading={loading}
          rowKey="id"
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
          }}
          onTableChange={handleTableChange}
        />
      </div>

      {/* <div className="text-center mt-[16px]">
        <Button
          buttonText="Assign"
          padding="py-[16px] px-[36px] lg:px-[80px]"
          text="text-[12px] 2xl:text-sm"
        />
      </div> */}
      <SideDrawer
        open={isModalOpen}
        onClose={handleIsModelOpen}
        type={"dataElement"}
        organizationData={selectedOrganization} // Pass only selected record
      />
      {deleteModelVisible && (
        <ConfirmDeleteModal
          handleConfirmDeleteModel={handleDeleteModel}
          type="delete"
          handleModelFunction={handleUserDeletion}
        />
      )}
    </div>
  );
};

export default DataElements;
